import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ReportData {
  headers: string[];
  rows: any[][];
  summary?: any;
  csv?: string;
}

const DailyReports: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [selectedReport, setSelectedReport] = useState<string>('');

  // Check if user is Super Admin
  const isSuperAdmin = user && (user.role_id === 1 || user.role_name === 'Super Admin');

  // Check if user is Super Admin and redirect if not
  useEffect(() => {
    if (!authLoading && !isSuperAdmin) {
      navigate('/reports');
    }
  }, [authLoading, isSuperAdmin, navigate]);

  // Don't render if not Super Admin
  if (!authLoading && !isSuperAdmin) {
    return null;
  }

  const generateReport = async (reportType: string, params: any = {}) => {
    setLoading(true);
    setError(null);
    setReportData(null);

    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/reports/generate/${reportType}${queryParams ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data.data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to generate report');
      }
    } catch (error) {
      setError('Network error occurred');
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!reportData?.csv) return;

    const blob = new Blob([reportData.csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const renderReportTable = () => {
    if (!reportData) return null;

    const { headers, rows, summary } = reportData;

    return (
      <div className="card">
        <div className="card-header">
          <h3>Report Results</h3>
          <div className="card-actions">
            <button onClick={downloadCSV} className="btn btn-secondary" disabled={!reportData.csv}>
              📄 Download CSV
            </button>
          </div>
        </div>

        {summary && (
          <div className="report-summary">
            <h4>Summary</h4>
            <div className="summary-grid">
              {Object.entries(summary).map(([key, value]) => (
                <div key={key} className="summary-item">
                  <span className="summary-label">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}:
                  </span>
                  <span className="summary-value">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{String(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">📅 Daily Auto Reports</h1>
        <p className="page-subtitle">Automated daily reports sent via email with CSV attachments</p>
      </div>

      <div className="reports-section">
        <div className="daily-reports-grid">
          <div className="alert alert-info">
            <h4>🕕 Schedule: Daily at 6:00 AM</h4>
            <p>The following reports are automatically generated and emailed every day:</p>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>📋 Daily Production Completions</h3>
            </div>
            <div className="card-content">
              <ul>
                <li>All production orders completed in the previous day</li>
                <li>Planned vs actual production quantities</li>
                <li>Production efficiency metrics</li>
                <li>Issues encountered during production</li>
              </ul>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>📦 Daily Bulk Usage</h3>
            </div>
            <div className="card-content">
              <ul>
                <li>Bulk materials consumed in production</li>
                <li>Usage by product and bulk material type</li>
                <li>Inventory impact calculations</li>
              </ul>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>📥 Daily Inventory Additions</h3>
            </div>
            <div className="card-content">
              <ul>
                <li>New inventory added to the system</li>
                <li>Production lots completed</li>
                <li>Manual inventory adjustments</li>
              </ul>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>🏭 Daily Production Orders</h3>
            </div>
            <div className="card-content">
              <ul>
                <li>New production orders created</li>
                <li>Creation reasons (manual, auto-inventory, etc.)</li>
                <li>Priority and status breakdown</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>⚙️ Configuration</h3>
          </div>
          <div className="card-content">
            <p>
              <strong>Email Recipients:</strong> Configure via environment variable
              DAILY_REPORT_RECIPIENTS
            </p>
            <p>
              <strong>Email Settings:</strong> Configure SMTP settings in environment variables
            </p>
            <p>Example: DAILY_REPORT_RECIPIENTS="manager@company.com,supervisor@company.com"</p>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>🧪 Test Daily Reports</h3>
          </div>
          <div className="card-content">
            <p>Generate today's reports to preview the daily email content:</p>
            <div className="report-buttons">
              <button
                onClick={() => {
                  setSelectedReport('production-completions');
                  generateReport('production-completions', {
                    date: new Date().toISOString().split('T')[0],
                  });
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Test Production Completions
              </button>

              <button
                onClick={() => {
                  setSelectedReport('bulk-usage');
                  generateReport('bulk-usage', { date: new Date().toISOString().split('T')[0] });
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Test Bulk Usage
              </button>

              <button
                onClick={() => {
                  setSelectedReport('inventory-additions');
                  generateReport('inventory-additions', {
                    date: new Date().toISOString().split('T')[0],
                  });
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Test Inventory Additions
              </button>

              <button
                onClick={() => {
                  setSelectedReport('production-orders');
                  generateReport('production-orders', {
                    date: new Date().toISOString().split('T')[0],
                  });
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Test Production Orders
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner">Generating report...</div>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        {reportData && renderReportTable()}
      </div>
    </div>
  );
};

export default DailyReports;
